import React, { Fragment, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import ParsedFormattedMessage from '@utils/miscellaneous/react-intl-utlis';
// Constants

import externalEndpoints from '@constants/configs/externalEndpoints';

// Types

import { IsState, Language } from '../../../SsapTypes';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';


interface IVoterAndOrganDonorInformation {
    exchangeName: string,
    isState: IsState,
    language: Language,
    REGISTER_FOR_VOTE: string,
    PHONE: string,
}

const VoterAndOrganDonorInformation = (props: IVoterAndOrganDonorInformation): ReactElement | null => {
    const {
        exchangeName,
        isState,
        language,
        PHONE,
        REGISTER_FOR_VOTE
    } = props;

    let resolveVoterRegistrationResource: {[key: string]: string} | string,
        resolveOrganDonorRegistrationResource: string;

    switch (true) {
        case isState.PA:
            resolveVoterRegistrationResource = externalEndpoints.PA_LINKS.VOTER_REGISTRATION;
            resolveOrganDonorRegistrationResource = externalEndpoints.PA_LINKS.ORGAN_DONOR_REGISTRATION;
            break;
        case isState.IL:
            resolveVoterRegistrationResource = externalEndpoints.IL_LINKS.VOTER_REGISTRATION;
            resolveOrganDonorRegistrationResource = externalEndpoints.IL_LINKS.ORGAN_DONOR_REGISTRATION;
            break;
        case isState.GI:
            resolveVoterRegistrationResource = externalEndpoints.GI_LINKS.VOTER_REGISTRATION;
            break;
        case isState.OR:
            resolveVoterRegistrationResource = externalEndpoints.OR_LINKS.VOTER_REGISTRATION;
            resolveOrganDonorRegistrationResource = externalEndpoints.OR_LINKS.ORGAN_DONOR_REGISTRATION;
            break;
        case isState.VA:
            resolveVoterRegistrationResource = externalEndpoints.VA_LINKS.VOTER_REGISTRATION;
            break;
        case isState.GA:
            resolveVoterRegistrationResource = externalEndpoints.GA_LINKS.VOTER_REGISTRATION;
            break;
        default:
    }
    
    const getDonorInformation = () => {
        switch(true) {
            case isState.NJ:
                return (
                    <>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.if_you_are_not_registered'
                            tagName = 'p'
                        />
                        <a
                            className = 'usa-link--external'
                            href = { `https://${ REGISTER_FOR_VOTE }` }
                            target = '_blank'
                            rel = 'noopener noreferrer'
                        >
                            { `https://${ REGISTER_FOR_VOTE }` }
                        </a>
                    </>
                );
            case isState.IL:
            case isState.OR:
            case isState.PA:
            case isState.MN:
                return (
                    <>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.if_you_are_not_registered_organ_donor'
                            tagName = 'p'
                            values = { {
                                exchangeName,
                                register: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.register' />
                                    </strong>
                                ),
                            } }
                        />

                        {
                            !isState.IL && !isState.OR && (
                                <a
                                    className = 'usa-link--external'
                                    id='organ_donor_registration_link'
                                    href = { resolveOrganDonorRegistrationResource }
                                    target = '_blank'
                                    rel = 'noopener noreferrer'
                                >
                                    { resolveOrganDonorRegistrationResource }
                                </a>
                            )
                        }

                        <FormattedMessage
                            id = 'ssap.sign_and_review.organ_donor_registration_note'
                            tagName = 'p'
                            values = { {
                                strong: chunks => <strong>{chunks}</strong>,
                                exchangeName,
                                note: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.note' />
                                    </strong>
                                ),
                                voluntary: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.voluntary' />
                                    </strong>
                                )
                            } }
                        />
                    </>
                );
            default:
                return null;
        }
    };
    const getVoterRegistration_VA = () => {
        return (
            <Fragment>
                <ParsedFormattedMessage
                    id = 'ssap.sign_and_review.if_you_are_not_registered'
                    tagName = 'p'
                    values = {{
                        link: 
                            <a
                                className='usa-link--external'
                                id='voter_registration_link'
                                href={ resolveVoterRegistrationResource as string }
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <FormattedMessage
                                    id = 'ssap.sign_and_review.if_you_are_not_registered_link_text'
                                    values={{ resolveVoterRegistrationResource: resolveVoterRegistrationResource as string }}
                                />
                            </a>
                        
                    } }
                />
                <ParsedFormattedMessage
                    id = 'ssap.sign_and_review.vote_registration_note'
                    tagName = 'p'
                    values = {{
                        exchangeName
                    } }
                />
            </Fragment>
        );
    };

    const getInformation = (): React.ReactElement | null => {
        switch(true) {
            case isState.NJ:
                return getDonorInformation();
            case isState.PA:
                return getDonorInformation();
            case isState.IL:
            case isState.OR:
            case isState.GA:
                return (
                    <Fragment>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.if_you_are_not_registered'
                            tagName = 'p'
                            values = { {
                                register: (
                                    <strong>
                                        <FormattedMessage id = 'ssap.sign_and_review.register' />
                                    </strong>
                                ),
                                update: (
                                    <strong>
                                        <FormattedMessage id = 'ssap.sign_and_review.update' />
                                    </strong>
                                )
                            } }
                        />

                        {
                            language && (
                                <a
                                    className = 'usa-link--external'
                                    id='voter_registration_link'
                                    href = { resolveVoterRegistrationResource[language] }
                                    target = '_blank'
                                    rel = 'noopener noreferrer'
                                >
                                    { resolveVoterRegistrationResource[language] }
                                </a>
                            )
                        }

                        <FormattedMessage
                            id = 'ssap.sign_and_review.vote_registration_note'
                            tagName = 'p'
                            values = { {
                                exchangeName,
                                note: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.note' />
                                    </strong>
                                ),
                                voluntary: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.voluntary' />
                                    </strong>
                                ),
                                phone: phoneNumberTools.getAdjustedPhoneNumber(PHONE),
                            } }
                        />
                        { getDonorInformation() }
                    </Fragment>
                );
            default:
                return null;
        }
    };

    return (isState.VA || isState.GI) ? getVoterRegistration_VA() : getInformation();
};

export default VoterAndOrganDonorInformation;